import React, { ChangeEvent, useState } from 'react';

import { MoreVert } from '@mui/icons-material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Student } from '../../services/students/types';
import { StudentState } from '../../types/common';
import Chip from '../Chip';
import Link from '../Link';
import Modal from '../Modal';
import ProfileIcon from '../ProfileIcon';

import { IconContainer } from './index.styles';
import { getStudentChipLabel } from './utils';

interface StudentRowProps {
  student: Student,
  captionAttributes?: (keyof Student)[],
  editable?: boolean,
  rollActive?: boolean,
  isPresent?: boolean,
  isMakeup?: boolean,
  isOnline?: boolean,
  studentDetailsHref?: string,
  onMark?: (studentId: string, present: boolean) => void,
  makeupHref?: string,
  onOnlineToggle?: (studentId: string) => void,
  handleLessonUnenrol?: (studentId: string) => void,
}

const getAttendanceIcon = (present: boolean) => {
  if (present) {
    return <CheckCircleOutlinedIcon color="success" />;
  }
  return <CancelOutlinedIcon color="error" />;
};

const StudentRow = ({
  student,
  captionAttributes = ['phoneNumber'],
  rollActive = true,
  editable = true,
  isPresent = false,
  isMakeup = false,
  isOnline = false,
  studentDetailsHref,
  onOnlineToggle,
  onMark = () => {},
  makeupHref,
  handleLessonUnenrol,
}: StudentRowProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unenrolFormOpen, setUnenrolFormOpen] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChecked = (event: ChangeEvent<HTMLInputElement>) => {
    onMark(student.id, event.target.checked);
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      position="relative"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
      >
        {rollActive && (
          <Stack direction="row" alignItems="center">
            {editable ? (
              <Tooltip title="Attendance">
                <Checkbox
                  size="medium"
                  checked={isPresent}
                  onChange={handleChecked}
                />
              </Tooltip>
            ) :
              getAttendanceIcon(isPresent)}
            {(makeupHref && editable) && (
              <Tooltip title="Reschedule">
                <IconButton disabled={isPresent} color="info" href={makeupHref}>
                  <ExitToAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
        <ProfileIcon
          firstName={student.firstName}
          lastName={student.lastName}
          color={student.profileIcon?.color}
          imageUrl={student.profileIcon?.image}
          size={36}
        />
        <Stack direction="column">
          <Typography fontWeight="bold">
            {studentDetailsHref ? (
              <Link href={studentDetailsHref}>
                {student.name}
              </Link>
            ) : student.name}
          </Typography>
          <Typography>
            {captionAttributes
              .map((captionAttribute) => student[captionAttribute as keyof Student])
              .filter((caption, i) => {
                // Do not render empty attributes as captions
                if (!caption) return false;
                // Only render the first caption on mobile
                if (!isMd && i !== 0) return false;
                return true;
              })
              .join(' | ')}
          </Typography>
        </Stack>
      </Stack>
      {(student.state === StudentState.PendingAttendance ||
        student.state === StudentState.OnTrial) && (
        <Chip
          label={getStudentChipLabel(student.state)}
          color={student.state === StudentState.PendingAttendance ? 'lightGreen' : 'lightYellow'}
          allCaps
        />
      )}
      {isMakeup && (
        <Chip
          label="Makeup"
          color="lightOrange"
          allCaps
        />
      )}
      {isOnline && (
        <Chip
          label="Online"
          color="lightBlue"
          allCaps
        />
      )}
      {(onOnlineToggle || handleLessonUnenrol) && (
        <IconContainer>
          <IconButton
            size="small"
            onClick={handleClick}
          >
            <MoreVert />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            {onOnlineToggle && (
              <MenuItem onClick={() => {
                handleClose();
                onOnlineToggle(student.id);
              }}
              >
                {isOnline ? 'Change to In Person' : 'Change to Online'}
              </MenuItem>
            )}
            {handleLessonUnenrol && (
              <MenuItem onClick={() => {
                handleClose();
                setUnenrolFormOpen(true);
              }}
              >
                One-off Unenrol
              </MenuItem>
            )}
          </Menu>
        </IconContainer>
      )}
      <Modal
        open={unenrolFormOpen}
        handleClose={() => setUnenrolFormOpen(false)}
        title="Unenrol Student"
        scrollY
        buttons={
          [
            {
              label: 'Unenrol',
              onClick: () => {
                if (handleLessonUnenrol) {
                  handleLessonUnenrol(student.id);
                  setUnenrolFormOpen(false);
                }
              },
            },
          ]
        }
      >
        <Typography>
          This student will be unenrolled for this lesson only.
          If they are doing a makeup, use the reschedule button instead!
        </Typography>
      </Modal>
    </Stack>
  );
};
export default StudentRow;
